/**
 * Thin wrapper over the PostHog global.
 *
 * Reviewers are identified by opaque ID only — never name or email. They are
 * third parties invited to a customer's review rather than RME users, and they
 * have not opted into our analytics. Everything worth segmenting on (which
 * course, which cycle, whether they hold an account) travels as properties
 * instead, so the funnels work without carrying anyone's contact details.
 *
 * index.html installs an inert `window.posthog` stub on sensitive routes, and a
 * blocker can remove the global entirely, so every call here is optional and
 * failures are swallowed: analytics must never break a review session.
 */

import { axiosErrorContext } from "./apiErrorContext";

const client = () => (typeof window === "undefined" ? null : window.posthog);

// Set on an error object once it has been reported. A failed request is often
// seen twice — the axios interceptor sees it first, then the component that
// rethrows it reaches Vue's errorHandler — and PostHog would otherwise count
// one broken endpoint as two.
const REPORTED = Symbol("rmeReported");

/**
 * Capture an event.
 * @param {string} event - snake_case, object_action (e.g. "comment_created").
 * @param {Object} [properties]
 */
export function track(event, properties = {}) {
  try {
    client()?.capture(event, properties);
  } catch (error) {
    // Deliberately ignored.
  }
}

/**
 * Report an exception to PostHog error tracking.
 *
 * `capture_exceptions` in index.html already picks up uncaught errors and
 * unhandled rejections. Use this for the ones the browser never reports as
 * uncaught: Vue render errors, and failures a `catch` block handled but that
 * still represent something broken.
 *
 * @param {*} error - Anything caught; non-Errors are wrapped so the event
 *   carries a stack instead of "[object Object]".
 * @param {Object} [properties] - Extra event properties for context.
 */
export function captureException(error, properties = {}) {
  const normalized = error instanceof Error ? error : new Error(String(error));

  if (wasReported(error)) return;
  markReported(error);

  // An AxiosError says only "Request failed with status code 500", so without
  // this every 500 in the app lands in one PostHog issue that cannot name the
  // endpoint that broke. Explicit properties still win.
  const context = { ...axiosErrorContext(error), ...properties };

  try {
    client()?.captureException(normalized, context);
  } catch (reportingError) {
    // Deliberately ignored.
  }
}

/**
 * @param {*} error
 * @returns {boolean} True if this error object was already sent to PostHog.
 */
export function wasReported(error) {
  return Boolean(error && typeof error === "object" && error[REPORTED]);
}

/**
 * Marks an error reported. Non-objects (a thrown string) cannot carry the flag;
 * they are rare and reporting one twice is cheaper than boxing every throw.
 *
 * @param {*} error
 */
function markReported(error) {
  if (!error || typeof error !== "object") return;
  try {
    Object.defineProperty(error, REPORTED, { value: true, enumerable: false });
  } catch (frozenError) {
    // A frozen error just gets reported twice.
  }
}

/**
 * Identify an account holder after login.
 *
 * Account holders are RME users who accepted our terms, so unlike reviewers
 * (below) their email and name may travel with the id. Goes through the
 * optional-global wrapper like everything else here: index.html installs an
 * inert stub when analytics are off for this build, and a blocker can remove
 * the global entirely, and login must succeed either way.
 *
 * @param {Object} user - The logged-in user record.
 */
export function identifyUser(user) {
  if (!user?._id) return;
  try {
    client()?.identify(String(user._id), { email: user.email, name: user.name });
  } catch (error) {
    // Deliberately ignored.
  }
}

/**
 * Identify a reviewer without attaching personal details.
 *
 * Prefers the linked user id when the reviewer also holds an RME account, so
 * their reviewing shows up as the same person across courses instead of a new
 * stranger per review cycle. Falls back to the reviewer record id otherwise.
 *
 * @param {Object} args
 * @param {string} [args.reviewerId] - Reviewer record id (the `rid` in the URL).
 * @param {string} [args.userId] - Linked RME user id, when there is one.
 * @returns {string|null} The distinct id used, or null if there was nothing to identify.
 */
export function identifyReviewer({ reviewerId, userId } = {}) {
  const distinctId = userId || reviewerId;
  if (!distinctId) return null;

  try {
    client()?.identify(String(distinctId), {
      role: "reviewer",
      has_account: Boolean(userId),
    });
  } catch (error) {
    // Deliberately ignored.
  }

  return String(distinctId);
}
